import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * useQuoteEntitlement — small hook that reads /api/quote/entitlements
 * and exposes a refresh() so callers can re-fetch after an action that
 * changes usage (e.g. a successful quote save or AI call).
 *
 * The shape mirrors the backend snapshot: plan, expires_at, source, limits{},
 * usage{}. When the request fails we return a "loading" state without any
 * inference — the caller must decide whether to gate the UI. This avoids
 * hiding features when the network is slow.
 */
export function useQuoteEntitlement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/quote/entitlements`);
      setData(res.data);
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isPro = Boolean(data?.limits?.advanced_pdf);
  return {
    entitlement: data,
    isPro,
    isFree: !!data && data.plan === 'free',
    loading,
    error,
    refresh,
  };
}

export default useQuoteEntitlement;
